import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function Autocomplete({
  value,
  onChange,
  suggestions = [],
  placeholder = "Type to search...",
  label,
  emptyMessage = "No results found.",
  minChars = 2,
  className = "",
  disabled = false,
  required = false,
  id,
  "data-testid": dataTestId,
  name
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (inputValue.length >= minChars) {
      const lowerQuery = inputValue.toLowerCase();
      const filtered = suggestions
        .filter(item => item.toLowerCase().includes(lowerQuery))
        .slice(0, 10);
      setFilteredSuggestions(filtered);
      if (filtered.length > 0 && !filtered.includes(inputValue)) {
        setOpen(true);
      }
    } else {
      setFilteredSuggestions([]);
      setOpen(false);
    }
  }, [inputValue, suggestions, minChars]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  const handleSelect = (selectedValue) => {
    setInputValue(selectedValue);
    onChange(selectedValue);
    setOpen(false);
  };

  // Handle blur to ensure value is committed (important for automated testing)
  const handleBlur = () => {
    // Close dropdown on blur
    setTimeout(() => setOpen(false), 150);
    // Ensure the current inputValue is committed
    if (inputValue !== value) {
      onChange(inputValue);
    }
  };

  return (
    <div className={cn("relative", className)} data-testid={dataTestId ? `${dataTestId}-wrapper` : undefined}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div>
            <Input
              ref={inputRef}
              id={id}
              name={name}
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder={placeholder}
              disabled={disabled}
              required={required}
              autoComplete="off"
              data-testid={dataTestId}
              onFocus={() => {
                if (filteredSuggestions.length > 0 && inputValue.length >= minChars) {
                  setOpen(true);
                }
              }}
              className="w-full"
            />
          </div>
        </PopoverTrigger>
        {filteredSuggestions.length > 0 && (
          <PopoverContent 
            className="w-[var(--radix-popover-trigger-width)] p-0" 
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="max-h-60 overflow-y-auto">
              {filteredSuggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className={cn(
                    "px-3 py-2 cursor-pointer text-sm hover:bg-slate-100 transition-colors",
                    suggestion === inputValue && "bg-slate-100"
                  )}
                  onClick={() => handleSelect(suggestion)}
                >
                  <div className="flex items-center">
                    {suggestion === inputValue && <Check className="w-4 h-4 mr-2 text-emerald-600" />}
                    <span className={suggestion === inputValue ? "font-medium" : ""}>{suggestion}</span>
                  </div>
                </div>
              ))}
            </div>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
}
